import type { CSSProperties } from 'react'

interface Box {
  top: number
  left: number
  right: number
  bottom: number
}

interface SpotlightProps {
  rect: DOMRect
  padding?: number
  /**
   * Controls which part of the spotlighted area, if any, remains clickable:
   * - 'full': the entire spotlighted box is clickable (e.g. the New Claim button, or the
   *   Vehicle Type field — the only two controls meant to be interacted with mid-tour).
   * - a DOMRect: only that sub-region within the spotlighted box is clickable.
   * - null/undefined: nothing in the spotlighted box is clickable — the step is narration
   *   only, and the user must use Next/Previous to proceed.
   */
  interactive?: 'full' | DOMRect | null
}

const bandStyle = 'fixed bg-black/45 backdrop-blur-[1.5px] tour-overlay-fade pointer-events-auto'

/** Renders 4 divs covering `outer` minus `inner`, using the given className for each band. */
function FrameBands({ outer, inner, className, style }: { outer: Box; inner: Box; className: string; style?: CSSProperties }) {
  const top = Math.max(inner.top, outer.top)
  const left = Math.max(inner.left, outer.left)
  const right = Math.min(inner.right, outer.right)
  const bottom = Math.min(inner.bottom, outer.bottom)

  return (
    <>
      <div className={className} style={{ ...style, top: outer.top, left: outer.left, width: outer.right - outer.left, height: Math.max(top - outer.top, 0) }} />
      <div className={className} style={{ ...style, top: bottom, left: outer.left, width: outer.right - outer.left, height: Math.max(outer.bottom - bottom, 0) }} />
      <div className={className} style={{ ...style, top, left: outer.left, width: Math.max(left - outer.left, 0), height: Math.max(bottom - top, 0) }} />
      <div className={className} style={{ ...style, top, left: right, width: Math.max(outer.right - right, 0), height: Math.max(bottom - top, 0) }} />
    </>
  )
}

/**
 * Full-viewport dark overlay with a cut-out around `rect`, built from four bands
 * instead of clip-path so each band can transition independently and cheaply.
 * A separate glowing ring is drawn on top of the cut-out rather than mutating the
 * real target element's box-shadow/transform — some targets (e.g. the claims table)
 * rely on their own overflow/sticky-header behavior, which a transform would disturb.
 */
export function Spotlight({ rect, padding = 8, interactive = null }: SpotlightProps) {
  const cutout: Box = {
    top: Math.max(rect.top - padding, 0),
    left: Math.max(rect.left - padding, 0),
    right: rect.right + padding,
    bottom: rect.bottom + padding,
  }
  const viewport: Box = { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight }

  return (
    <div className="fixed inset-0 z-[900] pointer-events-none" aria-hidden="true">
      {/* Darken everything outside the spotlighted box */}
      <FrameBands outer={viewport} inner={cutout} className={bandStyle} />

      {/* Optionally seal off (or partially seal) the spotlighted box itself so only the
          intended control (if any) stays clickable — everything else is narration-only. */}
      {interactive === null && (
        <div
          className="fixed pointer-events-auto"
          style={{ top: cutout.top, left: cutout.left, width: cutout.right - cutout.left, height: cutout.bottom - cutout.top }}
        />
      )}
      {interactive && interactive !== 'full' && (
        <FrameBands
          outer={cutout}
          inner={{ top: interactive.top, left: interactive.left, right: interactive.right, bottom: interactive.bottom }}
          className="fixed pointer-events-auto"
        />
      )}

      {/* Glowing highlight ring — purely decorative, sits above the overlay without touching the real target */}
      <div
        className="tour-highlight-ring fixed"
        style={{
          top: cutout.top,
          left: cutout.left,
          width: cutout.right - cutout.left,
          height: cutout.bottom - cutout.top,
        }}
      />
    </div>
  )
}
